import { depot } from '#~/depot/index.js';
import type { EventProperties } from '../types/properties.js';
import type { Room } from '@studio/models/room';
import { castId } from '@studio/models';

type PostHogGroups = Record<string, string>;

/** PostHog group type that attributes an event to an organization. */
export const ORGANIZATION_GROUP_TYPE = 'org';

/** The event properties that can say which organization an action belongs to. */
export interface OrganizationInputs {
  org_id?: unknown;
  orgId?: unknown;
  room_id?: unknown;
  roomId?: unknown;
  studio_id?: unknown;
  studioId?: unknown;
}

/** An id is usable only as a non-empty string; encoder events send `null`. */
const idOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/** The organization that owns a studio, from the room records this session loaded. */
export const resolveStudioOrganizationId = (studioId: string): string | undefined => {
  const roomRecord = depot.getState().roomRecords[castId<Room['id']>(studioId)];
  return idOf(roomRecord?.organizationId);
};

/** The room or studio an event names itself; a room and a studio share one id. */
export const namedStudioId = (properties: OrganizationInputs): string | undefined =>
  idOf(properties.room_id) ??
  idOf(properties.roomId) ??
  idOf(properties.studio_id) ??
  idOf(properties.studioId);

/**
 * The organization an event's action belongs to, or undefined when the action
 * has no known organization.
 *
 * An organization the event names comes first: a preset receipt freezes it
 * when the operator acts, because the room's organization can change while the
 * action completes, and an organization-settings action names the organization
 * it changes even while another organization's studio is on screen. Otherwise
 * the organization that owns the room or studio the event names. Never the
 * account's membership list: outside a studio a user in several organizations
 * has no single current one, and choosing by list position attributes the
 * action to an unrelated organization.
 */
export const resolveEventOrganizationId = (properties: OrganizationInputs): string | undefined => {
  const namedOrgId = idOf(properties.org_id) ?? idOf(properties.orgId);
  if (namedOrgId !== undefined) {
    return namedOrgId;
  }
  const studioId = namedStudioId(properties);
  return studioId === undefined ? undefined : resolveStudioOrganizationId(studioId);
};

/**
 * Resolves the correct `$groups` payload for a PostHog event based on
 * the properties already present on the enriched event.
 *
 * Context rules:
 * - Unauthenticated / login screen → no groups
 * - Action with a known organization but no studio → `org` only
 * - Inside a studio, no specific device → `org` + `studio`
 * - Device-specific action → `org` + `studio` + `camera_model` + `camera_brand`
 */
export const resolveEventGroups = (
  properties: EventProperties | undefined
): PostHogGroups | undefined => {
  if (!properties) {
    return undefined;
  }

  const groups: PostHogGroups = {};

  const studioId = properties.studio_id ?? properties.studioId;
  const orgId = resolveEventOrganizationId(properties);
  const deviceBrandId = properties.device_brand_id;
  const deviceModelId = properties.device_model_id;

  if (orgId !== undefined) {
    groups[ORGANIZATION_GROUP_TYPE] = orgId;
  }

  if (studioId) {
    groups.studio = studioId;
  }

  if (deviceBrandId) {
    groups.camera_brand = deviceBrandId;
  }

  if (deviceModelId) {
    groups.camera_model = deviceModelId;
  }

  return Object.keys(groups).length > 0 ? groups : undefined;
};
